import { getPanelShadow } from "common/NewLook/tokens.ts";
import Card from "common/Boxes/Card";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import React, { memo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled, { useTheme } from "styled-components";
import CoinCard from "features/dashboard/Accounts/CoinCard";
import {
  getFirstLastInGraphData,
  getPriceChangePercentage,
} from "features/dashboard/common/utils";
import {
  DISPLAY_CURRENCIES,
  MOBILE_VIRTUAL_LIST,
  TIME_RANGE_1_DAY,
} from "common/constants";
import PriceChange from "common/Charts/PriceChange";
import Flex from "common/Boxes/Flex";
import CardChart from "features/dashboard/CardChart";
import { getAccountExtraDetailsRoute } from "utils/routes";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import {
  ACCOUNT_CARD_HEIGHT,
  ACCOUNT_CARD_WIDTH,
  COIN_CARD_HEIGHT,
  MOBILE_ACCOUNT_CARD_HEIGHT,
  MOBILE_ACCOUNT_CARD_WIDTH,
} from "features/dashboard/common/constants";
import { skipToken } from "@reduxjs/toolkit/query";
import { selectPreprocessedPortfolioData } from "@shared/slices/portfolio.slice";
import IconWithFallback from "Global/IconWithFallback";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import useMedia from "utils/hooks/useMedia";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import { isNumber } from "lodash";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { formatTimeSeriesChartData } from "@shared/utils/charts";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { ICON_TYPES } from "@shared/common/constants";
import {
  ClientData,
  ConsolidatedAccountData,
  LanguageNameSpaces,
  PrecisionType,
  ProcessedCoinData,
} from "@shared/types";
import EmptyView from "common/Empty/EmptyView";
import { typography } from "@shared/common/Themes/typography";
import { sharedLib } from "@shared/services/shared.service";
import { EmptyStatesType } from "common/Icons/types.ts";
import { getCoinCardKey } from "@shared/features/portfolio/utils.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface AccountCardProps {
  accountPortfolioData: ConsolidatedAccountData | ClientData;
}

const AccountCard = ({ accountPortfolioData }: AccountCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const isMobile = useMedia(
    [`(max-width: ${MOBILE_VIRTUAL_LIST})`],
    [true],
    false
  );
  const NUM_OF_TOP_COINS = isMobile ? 2 : 3;
  const appTheme = useTheme();

  const [selectedTimeRange, setSelectedTimeRange] = useState(TIME_RANGE_1_DAY);
  const hasFullInfo = "client" in accountPortfolioData;
  const { isLoading: getPortfolioDataLoading } =
    sharedLib.sharedApi.portfolio.endpoints.getPortfolioData.useQueryState(
      undefined
    );
  const {
    data: accountExtraInfo,
    isFetching: accountExtraInfoFetching,
    isSuccess: accountExtraInfoSuccess,
  } = sharedLib.sharedApi.portfolio.useGetAccountExtraInfoGeneralQuery(
    accountPortfolioData.client_id
      ? {
          account_id: accountPortfolioData.client_id,
          time_range: selectedTimeRange,
        }
      : skipToken
  );

  const coinsData: ProcessedCoinData[] = useSelector(
    selectPreprocessedPortfolioData
  );

  const displayCurrency = useSelector(selectDisplayCurrency);
  const { historyPush } = useHistoryWithQuery();
  const [changePercentage, setChangePercentage] = useState<number>();
  const [changeValue, setChangeValue] = useState<string | React.JSX.Element>();

  const getAccountName = (
    accountData: ConsolidatedAccountData | ClientData
  ) => {
    return "client" in accountData
      ? accountData.client
      : accountData.client_name;
  };

  const filteredCoinsArray = coinsData.filter(
    (coin) => coin.client === getAccountName(accountPortfolioData)
  );
  const coinsArrayLength = filteredCoinsArray.length;
  const noCoins = !coinsArrayLength && !getPortfolioDataLoading;
  const noCoinsEmpty = (
    <EmptyView
      iconKey={EmptyStatesType.WideCoins}
      title={t("account.no_coins_title")}
      titleStyle={{ fontSize: typography.fontSizes.M, marginTop: 0 }}
      iconWidth={120}
    />
  );

  const graphData =
    accountExtraInfo?.data.graph_data?.[selectedTimeRange] || [];
  const precision =
    displayCurrency === DISPLAY_CURRENCIES.USD
      ? accountExtraInfo?.precision?.balance.USD
      : accountExtraInfo?.precision?.balance.BTC;
  const formattedGraphData = formatTimeSeriesChartData(
    graphData,
    displayCurrency === DISPLAY_CURRENCIES.USD ? 1 : 2,
    true,
    null,
    precision
  );

  const priceIndexInGraphData =
    displayCurrency === DISPLAY_CURRENCIES.USD ? 1 : 2;

  useEffect(() => {
    const [first, last] = getFirstLastInGraphData(
      graphData,
      priceIndexInGraphData
    );
    if (!accountExtraInfoFetching) {
      const changeByCurrency =
        last - first ? (
          <TextWithPrecisionAndIcon
            text={last - first}
            precisionConfig={accountExtraInfo?.precision}
            precisionType={PrecisionType.BALANCE}
            iconType={ICON_TYPES.DISPLAY_CURRENCY}
          />
        ) : undefined;
      setChangeValue(changeByCurrency);
      setChangePercentage(getPriceChangePercentage(first, last));
    }
  }, [graphData, displayCurrency, priceIndexInGraphData]);

  const totalByCurrency = hasFullInfo ? (
    <TextWithPrecisionAndIcon
      text={
        displayCurrency === DISPLAY_CURRENCIES.USD
          ? accountPortfolioData.total_in_usd
          : accountPortfolioData.total_in_btc
      }
      precisionConfig={accountExtraInfo?.precision}
      precisionType={PrecisionType.BALANCE}
      iconType={ICON_TYPES.DISPLAY_CURRENCY}
    />
  ) : undefined;

  const getTopCoinCards = () => {
    return filteredCoinsArray
      .sort((a, b) => b.total_in_usd - a.total_in_usd)
      .slice(0, NUM_OF_TOP_COINS);
  };

  const openAccountExtraDetails = () => {
    historyPush(
      getAccountExtraDetailsRoute(
        location.pathname,
        accountPortfolioData.client_id
      )
    );
  };

  return (
    <StyledCard $isMobile={isMobile}>
      <div onClick={openAccountExtraDetails}>
        <TitleWrapper style={{ overflow: "hidden" }}>
          <Flex $justifyContent="start" style={{ overflow: "hidden" }}>
            <IconWithFallback
              iconType={ICON_TYPES.EXCHANGE}
              coinOrExchangeName={accountPortfolioData.exchange}
              iconSize="xl"
            />
            <AccountTitle
              $ellipsis={true}
              $size={isMobile ? typography.fontSizes.M : typography.fontSizes.L}
            >
              {getAccountName(accountPortfolioData)}
            </AccountTitle>
          </Flex>
          <Title level={isMobile ? 4 : 3} type="secondary">
            {totalByCurrency}
          </Title>
        </TitleWrapper>
        <TitleWrapper $justifyContent="flex-end">
          <StyledTitle level={isMobile ? 5 : 4} type="secondary">
            {changeValue}
          </StyledTitle>
          {isNumber(changePercentage) && (
            <PriceChange
              change={changePercentage}
              size={appTheme.typography.pxFontSizes.S}
              useBackground={true}
            />
          )}
        </TitleWrapper>
      </div>
      <CardChart
        graphData={formattedGraphData}
        loading={accountExtraInfoFetching}
        selectedTimeRange={selectedTimeRange}
        setSelectedTimeRange={setSelectedTimeRange}
        precision={precision}
        isSuccess={accountExtraInfoSuccess}
        height={isMobile ? 100 : 120}
      />
      <TopCoinsWrapper>
        <TitleWrapper
          $justifyContent="flex-start"
          onClick={openAccountExtraDetails}
        >
          <StyledTitle level={4}>{t("account.top_coins_title")}</StyledTitle>
          {coinsArrayLength > NUM_OF_TOP_COINS && (
            <Title level={5} type="secondary">
              {t("account.top_coins_out_of", { count: coinsArrayLength })}
            </Title>
          )}
        </TitleWrapper>
        <CoinCardsWrapper $noCoins={noCoins}>
          <LoadingSpinnerOverlay
            isLoading={getPortfolioDataLoading}
            backgroundColor={appTheme.colors.cardBgColor}
          >
            {noCoins && noCoinsEmpty}
            <EqualColumnsGrid numColumns={isMobile ? 2 : 3} columnGap={8}>
              {getTopCoinCards().map((coin) => (
                <CoinCard
                  key={getCoinCardKey(coin)}
                  coinData={coin}
                  exchange={accountPortfolioData.exchange}
                  precision={accountExtraInfo?.precision}
                />
              ))}
            </EqualColumnsGrid>
          </LoadingSpinnerOverlay>
        </CoinCardsWrapper>
      </TopCoinsWrapper>
    </StyledCard>
  );
};
const MemoizedAccountCard = memo(AccountCard);
export default MemoizedAccountCard;

const StyledCard = styled(Card)<{ $isMobile: boolean }>`
  background: ${(p) => p.theme.colors.cardBgColor};
  height: ${(p) =>
    p.$isMobile
      ? `${MOBILE_ACCOUNT_CARD_HEIGHT}px`
      : `${ACCOUNT_CARD_HEIGHT}px`};
  width: ${(p) =>
    p.$isMobile ? `${MOBILE_ACCOUNT_CARD_WIDTH}px` : `${ACCOUNT_CARD_WIDTH}px`};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: ${(p) =>
    p.theme.isNewLook
      ? getPanelShadow(p.theme)
      : p.theme.shadows.dashboardCard};
  cursor: pointer;
  padding: ${(p) => (p.$isMobile ? 12 : 16)}px;
`;
const TitleWrapper = styled(Flex)`
  margin-bottom: 8px;
`;
const StyledTitle = styled(Title)`
  margin-right: 8px;
`;
const CoinCardsWrapper = styled.div<{ $noCoins: boolean }>`
  ${(props) => props.$noCoins && `justify-content: center;`}
  height: ${COIN_CARD_HEIGHT}px;
`;
const TopCoinsWrapper = styled.div`
  padding-top: 8px;
  position: relative;
`;
const AccountTitle = styled(Text)`
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  margin: 0 6px;
  white-space: nowrap;
`;
